import { createContext, useState, useContext, useEffect } from "react";
import { baseURL, baseAxios, authenticatedAxios } from "../apis";
import { useNavigate, useLocation } from "react-router-dom";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [admin, setAdmin] = useState(null);
  const [csrf, setCsrf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState("");

  // Login function - simulates API call but always succeeds
  const login = async (email, password) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await baseAxios.post(
        `${baseURL}/api/admin/login/`,
        { email, password },
        {
          headers: {
            "X-csrftoken": csrf,
          },
        },
      );
      const { refresh_token, access_token, ...restuser } = data.user;
      // ✅ Namespaced keys — won't collide with user portal tokens
      // localStorage.setItem("access_token", access_token);
      // localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("admin_access_token", access_token);
      localStorage.setItem("admin_refresh_token", refresh_token);
      setIsAuthenticated(true);
      setAdmin(restuser);
    } catch (err) {
      // ✅ Fixed: was err.response?.error (always undefined, data is nested)
      // err.response?.error ??
      // err.response?.detail ??
      setError(
        err.response?.data?.error ??
          err.response?.data?.detail ??
          "Something happened, try again later",
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout function - simulates API call
  const logout = async () => {
    setAdmin(null);
    setCsrf(null);
    // setLoading(true);  // removed — unpaired, never reset on this path
    setIsAuthenticated(false);
    setError("");
    try {
      const refreshToken = localStorage.getItem("admin_refresh_token");
      await authenticatedAxios.post(`${baseURL}/api/admin/logout/`, {
        refresh_token: refreshToken,
      });
    } catch (err) {
      // Non-blocking — clear tokens regardless
      console.warn("Logout API call failed:", err.message);
    } finally {
      // ✅ Only remove admin keys, not entire localStorage
      // localStorage.clear();
      // sessionStorage.clear();
      localStorage.removeItem("admin_access_token");
      localStorage.removeItem("admin_refresh_token");
      window.location.href = "/login";
    }
  };

  const getCsrfToken = async () => {
    try {
      const { data } = await baseAxios.get(`${baseURL}/api/csrf-cookie/`);
      setCsrf(data.csrftoken);
      return true;
    } catch (err) {
      console.log(`Unable to get Csrf Token: ${err.message}`);
      throw err;
    }
  };

  const checkAuth = async () => {
    // ✅ Early exit — don't call API if no token exists
    const token = localStorage.getItem("admin_access_token");
    if (!token) throw new Error("No token");

    // try {
    const { data } = await authenticatedAxios.get(
      `${baseURL}/api/admin/check_login/`,
    );
    setAdmin(data.user);
    setIsAuthenticated(true);
    // } catch (err) {
    //   console.log(`Unable to check auth: ${err.message}`);
    //   throw err;
    // }
  };

  useEffect(() => {
    if (csrf === null) {
      setLoading(true);
      getCsrfToken()
        .then(() => {
          checkAuth()
            .catch((err) => {
              if (location.pathname !== "/login") {
                navigate("/login?origin=" + location.pathname);
              }
            })
            .finally(() => setIsAuthChecked(true));
        })
        // .catch((err) => {
        //   console.log(err);
        // })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [csrf, location.pathname, navigate]);

  const value = {
    admin,
    isAuthenticated,
    loading,
    error, // No errors in frontend-only mode
    login,
    logout,
  };

  if (
    csrf === null &&
    !isAuthChecked &&
    loading &&
    location.pathname !== "/login"
  )
    return <></>;
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default useAuth;

// const defaultAdmin = {
//   id: 0,
//   name: 'Admin User',
//   email: 'admin@example.com',
//   is_superuser: false
// };